/**
 * The ask, and the only interactive thing on the page.
 *
 * ## THE TWO OUTCOMES ARE SAID DIFFERENTLY, AND THAT IS THE POINT
 *
 * `sent` promises a reply, because the enquiry is now a row on the admin
 * panel's Interests tab. `failed` keeps everything they typed on screen and
 * gives the address to write to instead, since the reader cannot act on a
 * status code.
 *
 * ## WHAT IT ASKS FOR, AND WHAT IT DOES NOT
 *
 * A name, a company, an email and an optional message. No phone number, no
 * industry, no dropdown of budgets: every extra field costs enquiries, and
 * the purpose of this form is to start a conversation rather than to qualify
 * one. Nothing is sent anywhere until the button is pressed.
 *
 * The site rebuild made this the page's closing call, "Try Wend once", beside
 * the animated mark. Its request is unchanged — the same four fields, the same
 * honeypot, the same POST /public/interests through `lib/interest.ts` (the
 * public intake's body is `.strict()`, so a new field would be a contract, API
 * and migration change). Only the words around it moved: the message's label
 * no longer asks what channels the reader sends on, because the page no longer
 * sells channels.
 *
 * The off-screen field is a honeypot for form-filling bots — see
 * `lib/interest.ts`. It is hidden from people and from assistive technology and
 * out of the tab order. Autofill is the risk that matters: a filled honeypot is
 * answered "thank you" and stored nowhere, so a password manager that fills it
 * loses a real enquiry silently. Hence a `name` that means nothing to an
 * autofill heuristic (not "website" or "url", which identity profiles fill),
 * `autocomplete="new-password"`, which Chrome honours where it ignores "off",
 * and the 1Password, LastPass and Bitwarden opt-out attributes. That lowers the
 * risk; it cannot make it zero, and the trade is a deliberate one.
 */
import { useId, useState } from 'react';

import { Section } from '../components/site-chrome';
import { WendMarkAnimated } from '../components/WendMarkAnimated';
import {
  CONTACT_EMAIL,
  emptyEnquiry,
  enquiryProblems,
  submitInterest,
  type InterestEnquiry,
  type InterestOutcome,
} from '../lib/interest';

type Status = 'idle' | 'submitting' | 'done';

export function Interest() {
  const fieldId = useId();
  const [enquiry, setEnquiry] = useState<InterestEnquiry>(emptyEnquiry);
  const [problems, setProblems] = useState<Readonly<Record<string, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [outcome, setOutcome] = useState<InterestOutcome | null>(null);

  const set = (key: keyof InterestEnquiry) => (value: string) => {
    setEnquiry((previous) => ({ ...previous, [key]: value }));
  };

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (status === 'submitting') return;

    const found = enquiryProblems(enquiry);
    setProblems(found);
    if (Object.keys(found).length > 0) return;

    setStatus('submitting');
    const result = await submitInterest(enquiry);
    setOutcome(result);
    if (result.kind === 'failed' && result.problems !== undefined) setProblems(result.problems);
    /* A failure returns to `idle`: the form must stay on screen with what they
       typed still in it, or they lose the message they just wrote. */
    setStatus(result.kind === 'failed' ? 'idle' : 'done');
  }

  return (
    <Section id="talk" className="glow-brand">
      <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
        <div className="reveal flex flex-col items-center text-center lg:items-start lg:text-left">
          <WendMarkAnimated size={112} variant="drawFade" speed="slow" />
          <h2 className="mt-8 text-balance text-4xl font-semibold tracking-tight text-text sm:text-5xl">Try Wend once.</h2>
          <p className="mt-4 max-w-md text-pretty text-lg leading-relaxed text-muted">
            Half an hour, on your own list. We reply within a working day.
          </p>
          <p className="mt-5 text-[0.92rem] text-muted">
            Or write to{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="rounded-base text-text underline decoration-brand-a underline-offset-4 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>

        <div className="rounded-lg border border-line-strong bg-ink/80 p-6 shadow-lift sm:p-8">
          {outcome !== null && outcome.kind !== 'failed' ? (
            <div role="status" className="flex flex-col gap-3">
              <h3 className="text-xl font-semibold tracking-tight text-text">
                Thank you. That reached us.
              </h3>
              <p className="text-pretty leading-relaxed text-muted">
                We read every one of these ourselves and will reply within a working day.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="relative flex flex-col gap-5">
              <Field id={`${fieldId}-name`} label="Your name" problem={problems['name']}>
                {(controlId, invalid, describedBy) => (
                  <input
                    id={controlId}
                    value={enquiry.name}
                    onChange={(event) => set('name')(event.target.value)}
                    autoComplete="name"
                    maxLength={200}
                    aria-invalid={invalid}
                    aria-describedby={describedBy}
                    className={inputClass(invalid)}
                  />
                )}
              </Field>

              <Field id={`${fieldId}-org`} label="Company" problem={problems['organisation']}>
                {(controlId, invalid, describedBy) => (
                  <input
                    id={controlId}
                    value={enquiry.organisation}
                    onChange={(event) => set('organisation')(event.target.value)}
                    autoComplete="organization"
                    maxLength={200}
                    aria-invalid={invalid}
                    aria-describedby={describedBy}
                    className={inputClass(invalid)}
                  />
                )}
              </Field>

              <Field id={`${fieldId}-email`} label="Work email" problem={problems['email']}>
                {(controlId, invalid, describedBy) => (
                  <input
                    id={controlId}
                    type="email"
                    value={enquiry.email}
                    onChange={(event) => set('email')(event.target.value)}
                    autoComplete="email"
                    maxLength={254}
                    aria-invalid={invalid}
                    aria-describedby={describedBy}
                    className={inputClass(invalid)}
                  />
                )}
              </Field>

              <Field id={`${fieldId}-message`} label="Anything we should know?" optional>
                {(controlId) => (
                  <textarea
                    id={controlId}
                    value={enquiry.message}
                    onChange={(event) => set('message')(event.target.value)}
                    rows={4}
                    maxLength={5000}
                    className={`${inputClass(false)} resize-y`}
                  />
                )}
              </Field>

              <div aria-hidden="true" className="absolute -left-[10000px] top-auto size-px overflow-hidden">
                <label htmlFor={`${fieldId}-hp`}>Leave this empty</label>
                <input
                  id={`${fieldId}-hp`}
                  name="hp_x7"
                  type="text"
                  tabIndex={-1}
                  autoComplete="new-password"
                  data-1p-ignore=""
                  data-lpignore="true"
                  data-bwignore=""
                  value={enquiry.website}
                  onChange={(event) => set('website')(event.target.value)}
                />
              </div>

              {outcome?.kind === 'failed' && (
                <p role="alert" className="text-[0.88rem] text-bad">
                  {outcome.message}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-1 inline-flex items-center justify-center rounded-base bg-brand-a px-6 py-3 text-[0.95rem] font-medium text-white transition-colors hover:bg-brand-b disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
              >
                {status === 'submitting' ? 'Sending…' : 'Try Wend'}
              </button>

              {/* Said once, plainly, and only where it is true. */}
              <p className="text-[0.8rem] leading-relaxed text-muted">
                We use this to reply to you and nothing else.
              </p>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
}

function inputClass(invalid: boolean): string {
  return [
    'w-full rounded-base border bg-surface-2 px-3.5 py-2.5 text-[0.95rem] text-text',
    'placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a',
    invalid ? 'border-bad' : 'border-line',
  ].join(' ');
}

/** A label, its control, and the one sentence that says what is wrong with it. */
function Field({
  id,
  label,
  optional,
  problem,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly optional?: boolean;
  readonly problem?: string;
  readonly children: (controlId: string, invalid: boolean, describedBy: string | undefined) => React.ReactNode;
}) {
  const invalid = problem !== undefined;
  const problemId = `${id}-problem`;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="flex items-baseline gap-2 text-[0.85rem] font-medium text-text">
        {label}
        {optional === true && <span className="text-[0.75rem] font-normal text-muted">optional</span>}
      </label>
      {children(id, invalid, invalid ? problemId : undefined)}
      {/* Always in the tree, so a screen reader announces the sentence as it appears. */}
      <p id={problemId} aria-live="polite" className="text-[0.82rem] text-bad empty:hidden">
        {problem}
      </p>
    </div>
  );
}
